import { apiClient } from "../client";

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: { data: ApiError };
  message: string;
}

export interface HealthMetric {
  count: number;
  percentage: number;
  trend: "up" | "down" | "stable";
}

export interface AssetMetrics {
  total: HealthMetric;
  goodHealth: HealthMetric;
  needingRepair: HealthMetric;
  lostMissing: HealthMetric;
  complianceRate: HealthMetric;
  assetUtilization: HealthMetric;
}

export interface CategoryData {
  name: string;
  count: number;
  percentage: number;
  maintenanceIssues: number;
}

export interface LifecycleStateData {
  name: string;
  count: number;
  percentage: number;
}

export interface SiteUtilization {
  siteId: number;
  siteName: string;
  grossUtilizationPercentage: number;
  totalAssets: number;
  inUseAssets: number;
  netUtilizationPercentage: number;
  workingAssets: number;
  utilizationPercentage?: number;
  activeAssets?: number;
}

export interface TrendDataPoint {
  date: string;
  value: number;
  count: number;
}

export interface DepartmentHealthData {
  name: string;
  totalAssets: number;
  goodHealth: number;
  needingRepair: number;
  averageHealthPercentage: number;
}

export interface GlobalDashboard {
  organizationId: number;
  organizationName: string;
  totalSites: number;
  totalAssets: number;
  assetMetrics: AssetMetrics;
  topCategories: CategoryData[];
  lifecycleBreakdown: LifecycleStateData[];
  siteUtilization: SiteUtilization[];
  lastUpdated: Date;
  departmentHealth: DepartmentHealthData[];
}

export interface SiteDashboard {
  siteId: number;
  siteName: string;
  organizationId: number;
  organizationName: string;
  assetMetrics: AssetMetrics;
  lifecycleBreakdown: LifecycleStateData[];
  topCategories: CategoryData[];
  siteUtilization: SiteUtilization[];
  lastUpdated: Date;
  departmentHealth: DepartmentHealthData[];
}

export enum AgentType {
  HEALTH = "health",
  FINANCE = "finance",
  UTILIZATION = "utilization",
  COMPLIANCE = "compliance",
  MAINTENANCE = "maintenance",
}

export enum AlertSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

export enum AlertStatus {
  ACTIVE = "active",
  ACKNOWLEDGED = "acknowledged",
  RESOLVED = "resolved",
  DISMISSED = "dismissed",
}

export enum AlertScope {
  SITE = "site",
  GLOBAL = "global",
}

export interface AlertMetadata {
  affectedAssets?: number[];
  metricValue?: number;
  threshold?: number;
  trend?: string;
  estimatedImpact?: string;
  totalSites?: number;
  affectedSites?: number;
  siteSummaries?: Array<{
    siteId: number;
    siteName: string;
    value: number;
  }>;
  tasks?: Array<{
    id: string;
    title: string;
    info: string;
    assignee?: string;
    status: "pending" | "approved" | "ignored" | "sent";
    actionLabel?: string;
  }>;
}

export interface AgentAlert {
  id: number;
  agentType: AgentType;
  siteId: number | null;
  organizationId?: number | null;
  scope?: AlertScope;
  assetId?: number | null;
  severity: AlertSeverity;
  message: string;
  aiAnalysis?: string | null;
  actionRequired?: string | null;
  status: AlertStatus;
  metadata?: AlertMetadata | null;
  dismissedBy?: number | null;
  dismissedAt?: string | null;
  dismissalReason?: string | null;
  remindAt?: string | null;
  remindedBy?: number | null;
  reminderCount?: number;
  createdAt: string;
  updatedAt: string;
  asset?: {
    id: number;
    name: string;
    assetTagId?: string;
  } | null;
  site?: {
    id: number;
    name: string;
  } | null;
}

export interface AlertPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetAgentAlertsResponse {
  message: string;
  alerts: AgentAlert[];
  pagination: AlertPagination;
}

export interface AgentStatusEntry {
  lastRun: string | null;
  activeAlerts: number;
  isEnabled: boolean;
  status?: "Active" | "Inactive";
}

export interface AgentStatusAgents {
  health: AgentStatusEntry;
  finance: AgentStatusEntry;
  utilization: AgentStatusEntry;
  compliance: AgentStatusEntry;
  maintenance: AgentStatusEntry;
}

export interface SiteAgentStatusResponse {
  siteId: number;
  agents: AgentStatusAgents;
  totalActiveAlerts: number;
}

export interface OrgAgentStatusResponse {
  organizationId: number;
  agents: AgentStatusAgents;
  totalActiveAlerts: number;
}

export type AgentStatusResponse =
  | SiteAgentStatusResponse
  | OrgAgentStatusResponse;

export interface RunAgentResponse {
  message: string;
  agentType: string;
  siteId?: number;
  organizationId?: number;
  alertsGenerated: number;
  alerts: AgentAlert[];
}

export interface AlertActionResponse {
  message: string;
  alert: AgentAlert;
}

export interface GetAgentAlertsParams {
  siteId?: number;
  organizationId?: number;
  agentType?: AgentType;
  severity?: AlertSeverity;
  status?: AlertStatus;
  page?: number;
  limit?: number;
}

function buildTrendQuery(startDate?: string, endDate?: string): string {
  const params = new URLSearchParams();
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export class InsightService {
  static async getGlobalDashboard(
    organizationId: number,
  ): Promise<GlobalDashboard> {
    return apiClient.get<GlobalDashboard>(
      `/insights/dashboard/global/organizations/${organizationId}`,
    );
  }

  static async getSiteDashboard(siteId: number): Promise<SiteDashboard> {
    return apiClient.get<SiteDashboard>(`/insights/dashboard/site/${siteId}`);
  }

  static async getTotalAssetsTrend(
    siteId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/total-assets/site/${siteId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getGoodHealthTrend(
    siteId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/good-health/site/${siteId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getNeedingRepairTrend(
    siteId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/needing-repair/site/${siteId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getLostMissingTrend(
    siteId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/lost-missing/site/${siteId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getComplianceTrend(
    siteId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/compliance/site/${siteId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getAssetUtilizationTrend(
    siteId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/asset-utilization/site/${siteId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getGlobalTotalAssetsTrend(
    organizationId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/total-assets/organization/${organizationId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getGlobalGoodHealthTrend(
    organizationId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/good-health/organization/${organizationId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getGlobalNeedingRepairTrend(
    organizationId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/needing-repair/organization/${organizationId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getGlobalLostMissingTrend(
    organizationId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/lost-missing/organization/${organizationId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getGlobalComplianceTrend(
    organizationId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/compliance/organization/${organizationId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getGlobalAssetUtilizationTrend(
    organizationId: number,
    startDate?: string,
    endDate?: string,
  ): Promise<TrendDataPoint[]> {
    return apiClient.get<TrendDataPoint[]>(
      `/insights/trends/asset-utilization/organization/${organizationId}${buildTrendQuery(startDate, endDate)}`,
    );
  }

  static async getAgentAlerts(
    params: GetAgentAlertsParams,
  ): Promise<GetAgentAlertsResponse> {
    const query = new URLSearchParams();

    if (params.siteId != null) query.set("siteId", String(params.siteId));
    if (params.organizationId != null)
      query.set("organizationId", String(params.organizationId));
    if (params.agentType) query.set("agentType", params.agentType);
    if (params.severity) query.set("severity", params.severity);
    if (params.status) query.set("status", params.status);
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));

    return apiClient.get<GetAgentAlertsResponse>(
      `/insights/agents/alerts?${query.toString()}`,
    );
  }

  static async dismissAlert(
    alertId: number,
    reason?: string,
  ): Promise<AlertActionResponse> {
    return apiClient.patch<AlertActionResponse>(
      `/insights/agents/alerts/${alertId}/dismiss`,
      { reason },
    );
  }

  static async acknowledgeAlert(alertId: number): Promise<AlertActionResponse> {
    return apiClient.patch<AlertActionResponse>(
      `/insights/agents/alerts/${alertId}/acknowledge`,
      {},
    );
  }

  static async ignoreAlert(
    alertId: number,
    reason?: string,
  ): Promise<AlertActionResponse> {
    return apiClient.patch<AlertActionResponse>(
      `/insights/agents/alerts/${alertId}/ignore`,
      { reason },
    );
  }

  static async remindMeLater(
    alertId: number,
    reminderInMinutes?: number,
  ): Promise<AlertActionResponse> {
    return apiClient.patch<AlertActionResponse>(
      `/insights/agents/alerts/${alertId}/remind-later`,
      { reminderInMinutes: reminderInMinutes || 1440 },
    );
  }

  static async updateAlertTask(
    alertId: number,
    taskId: string,
    status: "approved" | "ignored",
  ): Promise<AlertActionResponse> {
    return apiClient.patch<AlertActionResponse>(
      `/insights/agents/alerts/${alertId}/tasks/${taskId}`,
      { status },
    );
  }

  static async runAgentForSite(
    agentType: AgentType | string,
    siteId: number,
  ): Promise<RunAgentResponse> {
    return apiClient.post<RunAgentResponse>(
      `/insights/agents/run/${agentType}/site/${siteId}`,
      {},
    );
  }

  static async runAgentForOrganization(
    agentType: AgentType | string,
    organizationId: number,
  ): Promise<RunAgentResponse> {
    return apiClient.post<RunAgentResponse>(
      `/insights/agents/run/${agentType}/organization/${organizationId}`,
      {},
    );
  }

  static async getAgentStatusForSite(
    siteId: number,
  ): Promise<SiteAgentStatusResponse> {
    return apiClient.get<SiteAgentStatusResponse>(
      `/insights/agents/status/site/${siteId}`,
    );
  }

  static async getAgentStatusForOrganization(
    organizationId: number,
  ): Promise<OrgAgentStatusResponse> {
    return apiClient.get<OrgAgentStatusResponse>(
      `/insights/agents/status/organization/${organizationId}`,
    );
  }

  /** @deprecated Use runAgentForSite instead */
  static async runAgent(
    agentType: AgentType | string,
    siteId: number,
  ): Promise<RunAgentResponse> {
    return InsightService.runAgentForSite(agentType, siteId);
  }

  /** @deprecated Use getAgentStatusForSite instead */
  static async getAgentStatus(
    siteId: number,
  ): Promise<SiteAgentStatusResponse> {
    return InsightService.getAgentStatusForSite(siteId);
  }

  static async activateSiteAgent(
    agentType: AgentType | string,
    siteId: number,
  ): Promise<{
    message: string;
    agentType: string;
    siteId: number;
    isEnabled: boolean;
  }> {
    return apiClient.post(
      `/insights/agents/${agentType}/site/${siteId}/activate`,
      {},
    );
  }

  static async deactivateSiteAgent(
    agentType: AgentType | string,
    siteId: number,
  ): Promise<{
    message: string;
    agentType: string;
    siteId: number;
    isEnabled: boolean;
  }> {
    return apiClient.post(
      `/insights/agents/${agentType}/site/${siteId}/deactivate`,
      {},
    );
  }

  static async activateOrgAgent(
    agentType: AgentType | string,
    organizationId: number,
  ): Promise<{
    message: string;
    agentType: string;
    organizationId: number;
    isEnabled: boolean;
  }> {
    return apiClient.post(
      `/insights/agents/${agentType}/organization/${organizationId}/activate`,
      {},
    );
  }

  static async deactivateOrgAgent(
    agentType: AgentType | string,
    organizationId: number,
  ): Promise<{
    message: string;
    agentType: string;
    organizationId: number;
    isEnabled: boolean;
  }> {
    return apiClient.post(
      `/insights/agents/${agentType}/organization/${organizationId}/deactivate`,
      {},
    );
  }
}
